












































































































import { Vue, Component, Watch } from "vue-property-decorator";
import { audiobooks } from "@/config/host.config";
import { deleteElement } from "@/api/delite/delite-element";
import { AxiosError } from "axios";
import ButtonBack from "@/components/buttons/button-back.vue";
import ButtonCart from "@/components/buttons/button-cart.vue";
import InputSearch from "@/components/app/search/input-search.vue";
import StandartTable from "@/components/tables/standart-table.vue";
import AudiobookTableCard from "@/components/tables/audiobooks/audiobook-table-card.vue";

interface IAudiobookName {
  [key: string]: string;
}

interface IAudiobookAuthor {
  name: string;
  ru?: string;
  en?: string;
  [key: string]: string | undefined;
}

interface IAudiobookFile {
  _id: string;
  name: { ru: string };
  filename: string;
  file_type: string;
  sort_order: number;
  language: string;
  duration: number;
}

interface IAudiobookSrc {
  file?: IAudiobookFile[];
  poster?: { ru: string };
  horizontel_poster?: { ru: string };
  vertical_poster?: { [key: string]: string };
  logo?: { ru: string };
}

interface IAudiobook {
  _id: string;
  name: IAudiobookName;
  length: number;
  description?: IAudiobookName;
  shortDescription?: IAudiobookName;
  ageRating?: string;
  year?: number;
  author?: IAudiobookAuthor[];
  narrator?: IAudiobookAuthor[];
  publisher?: string;
  languages?: string[];
  src?: IAudiobookSrc;
  category?: string;
  genres?: string[];
  ratings?: { average: number; count: number };
  show: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ISortColumn {
  name: number;
}

@Component({
  components: {
    ButtonBack,
    ButtonCart,
    InputSearch,
    StandartTable,
    AudiobookTableCard,
  },
})
class AudiobooksListPage extends Vue {
  bookList: Array<IAudiobook> = [];
  deleted = false;
  loading = false;
  search = "";
  sortKey = "";
  sortOrder: ISortColumn = { name: 1 };
  ddOpen = false;

  get filteredBooks(): Array<IAudiobook> {
    const query = this.search.trim().toLowerCase();
    const order = this.sortOrder[this.sortKey as keyof ISortColumn] || 1;

    let list = this.bookList;

    if (query) {
      list = list.filter((item) => {
        const name = this.getLocalizedName(item.name).toLowerCase();
        const authors = this.formatAuthors(item.author).toLowerCase();
        return name.indexOf(query) > -1 || authors.indexOf(query) > -1;
      });
    }

    if (this.sortKey == "name") {
      list = list.slice().sort((a: IAudiobook, b: IAudiobook) => {
        const one = this.getLocalizedName(a.name);
        const two = this.getLocalizedName(b.name);
        return (one === two ? 0 : one > two ? 1 : -1) * order;
      });
    }

    return list;
  }

  get tableEmpty(): string {
    if (this.bookList.length && !this.filteredBooks.length) {
      return String(this.$t("audiobooks.searchNotFound"));
    }
    return this.deleted
      ? String(this.$t("music.trashEmpty"))
      : String(
          this.$t("error.emptyList", { name: this.$t("audiobooks.title") })
        );
  }

  sortBy(key: string): void {
    this.sortKey = key;
    this.sortOrder[key as keyof ISortColumn] =
      this.sortOrder[key as keyof ISortColumn] * -1;
  }

  getLocalizedName(name: IAudiobookName | undefined): string {
    if (!name) return "—";
    return name[this.$i18n.locale] || name.ru || name.en || "—";
  }

  formatAuthors(authors: IAudiobookAuthor[] | undefined): string {
    if (!authors || !authors.length) return "—";
    return authors
      .map((a) => a[this.$i18n.locale] || a.ru || a.en || a.name)
      .join(", ");
  }

  async deleteBook(id: string): Promise<void> {
    const message = this.deleted
      ? String(this.$t("audiobooks.confirmDeleteBookForever"))
      : String(this.$t("audiobooks.confirmDeleteBook"));
    const ok = await this.$bvModal.msgBoxConfirm(message, {
      title: String(this.$i18n.t("buttons.delete")),
      okVariant: "danger",
      cancelVariant: "secondary",
    });
    if (!ok) return;

    if (this.deleted) {
      try {
        await this.$axios.delete(`${audiobooks.books}/force/${id}`);
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("error.200")),
        });
        this.fetchBooks();
      } catch (e) {
        console.log(e);
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("alerts.emptyUpdate")),
          type: "warn",
        });
      }
    } else {
      deleteElement(id, audiobooks.books)
        .then(() => {
          this.$notify({
            group: "global",
            text: String(this.$i18n.t("error.200")),
          });
          this.fetchBooks();
        })
        .catch((e: AxiosError) => {
          console.log(e);
          this.$notify({
            group: "global",
            text: String(this.$i18n.t("alerts.emptyUpdate")),
            type: "warn",
          });
        });
    }
  }

  async restoreBook(id: string): Promise<void> {
    try {
      const res = await this.$axios.put(`${audiobooks.books}/restore/${id}`);
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("error.200")),
        });
        this.fetchBooks();
      }
    } catch (e) {
      console.log(e);
      this.$notify({
        group: "global",
        text: String(this.$i18n.t("alerts.emptyUpdate")),
        type: "warn",
      });
    }
  }

  async toggleShow(id: string, show: boolean): Promise<void> {
    try {
      await this.$axios.put(`${audiobooks.books}/${id}`, { show });
    } catch (e) {
      console.log(e);
    }
  }

  @Watch("deleted")
  async onDeletedChange(): Promise<void> {
    this.$router
      .replace({
        query: {
          ...this.$route.query,
          deleted: this.deleted ? "true" : undefined,
        },
      })
      .catch(() => undefined);
    await this.fetchBooks();
  }

  async fetchBooks(): Promise<void> {
    this.loading = true;
    try {
      const params = { "match[deleted][value]": this.deleted };
      const res = await this.$axios.get(audiobooks.books, { params });
      if (res.status === 200) {
        this.bookList = res.data;
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  }

  mounted(): void {
    this.deleted = this.$route.query.deleted === "true";
    this.fetchBooks();
  }
}
export default AudiobooksListPage;
